"use client";

import Link from "next/link";
import { ArrowRight, LucideIcon } from "lucide-react";
import { Section, Container } from "@/components/ui/container";
import { Reveal } from "@/components/ui/reveal";

export interface SolutionPillar {
  title: string;
  body: string;
  icon: LucideIcon;
}

export interface SolutionSectionProps {
  id?: string;
  eyebrow: string;
  title: string;
  intro: string;
  pillars: SolutionPillar[];
  cta: { label: string; href: string };
  accent: string;
  pillarAccents: string[];
}

export function SolutionSection({
  id = "solution",
  eyebrow,
  title,
  intro,
  pillars,
  cta,
  accent,
  pillarAccents,
}: SolutionSectionProps) {
  return (
    <Section id={id} className="relative">
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-96 w-96 rounded-full opacity-20 blur-[140px]"
          style={{ background: accent }}
        />
      </div>

      <Container>
        <Reveal>
          <div className="max-w-3xl">
            <span className="eyebrow-tiny" style={{ color: accent }}>
              {eyebrow}
            </span>
            <h2 className="mt-3">{title}</h2>
            <p className="mt-5 text-body text-[rgb(var(--fg-muted))] leading-relaxed">{intro}</p>
          </div>
        </Reveal>

        <div className="mt-12 grid sm:grid-cols-2 gap-5">
          {pillars.map((p, i) => {
            const Icon = p.icon;
            const pAccent = pillarAccents[i % pillarAccents.length] ?? accent;
            return (
              <Reveal key={p.title} delay={i * 100}>
                <div
                  className="glass rounded-2xl p-7 h-full relative overflow-hidden"
                  style={{
                    boxShadow: `inset 0 0 0 1px color-mix(in oklch, ${pAccent} 18%, transparent)`,
                  }}
                >
                  <div
                    className="pointer-events-none absolute -top-24 -right-24 h-56 w-56 rounded-full opacity-20 blur-3xl"
                    style={{ background: pAccent }}
                  />
                  <div className="relative">
                    <span
                      className="grid place-items-center h-12 w-12 rounded-2xl"
                      style={{
                        background: `color-mix(in oklch, ${pAccent} 18%, transparent)`,
                        boxShadow: `inset 0 0 0 1px color-mix(in oklch, ${pAccent} 35%, transparent)`,
                      }}
                    >
                      <Icon className="h-5 w-5" style={{ color: pAccent }} />
                    </span>
                    <h3 className="mt-5">{p.title}</h3>
                    <p className="mt-3 text-body-sm text-[rgb(var(--fg-muted))] leading-relaxed">{p.body}</p>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>

        <Reveal delay={300}>
          <div className="mt-10">
            <Link
              href={cta.href}
              className="btn-primary-gradient h-11 px-6 inline-flex items-center gap-2 rounded-full font-semibold text-sm"
            >
              {cta.label} <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        </Reveal>
      </Container>
    </Section>
  );
}
